import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import SMCField from '../SMCField';
import {useSMCDisabledContext} from '../SMCDisabledContext';
import {hasValue} from '../../../utils/utils';

/**
 * @param {Object} params
 * @param {boolean} [params.disabled]
 * @param {(string|number)} [params.width]
 * @param {(string|number)} [params.height]
 * @param {(string|number)} [params.value] currently selected option
 * @param {boolean} [params.inline]
 * @param {function} [params.onChange]
 * @param {string} [params.className]
 * @param {Object} [params.style]
 * @param {string} [params.label]
 * @param {(string|number)} [params.labelWidth]
 * @param {string} [params.labelPosition]
 * @param {string} [params.tooltip]
 * @param {boolean} [params.required]
 * @param {boolean} [params.ignoreDisabledContext]
 * @param {boolean} [params.showEmptyOptionOnDisabled]
 */
function SMCSelect({
    disabled,
    width,
    height,
    value,
    inline,
    onChange,
    className,
    style,
    label,
    labelWidth,
    labelPosition,
    tooltip,
    required,
    ignoreDisabledContext,
    showEmptyOptionOnDisabled,
    children
}) {
    const classes = useStyles();
    const disabledContext = useSMCDisabledContext();
    
    const showEmptyOption = useMemo(() => showEmptyOptionOnDisabled && disabled, [showEmptyOptionOnDisabled, disabled]);
    
    const _disabled = useMemo(() => {
        if (disabledContext && !ignoreDisabledContext) {
            if (disabledContext.forceDisable) {
                return true;
            }
            if (disabledContext.forceEnable) {
                return false;
            }
        }
        if (hasValue(disabled)) {
            return disabled;
        }
        return undefined;
    }, [disabled, disabledContext, ignoreDisabledContext]);

    const selectElem = (
        <select
            style={{width: width ,...style}}
            value={value}
            disabled={_disabled}
            readOnly={_disabled}
            className={
                clsx(
                    classes.select,
                    className,
                    _disabled && classes.disabled,
                    'defaultInput'
                )
            }
            onChange={onChange}
        >
            {
                showEmptyOption ? (
                    <option />
                ) : (
                    children
                )
            }
        </select>
    );

    return (
        <SMCField
            disabled={_disabled}
            height={height}
            inline={inline}
            label={label}
            labelWidth={labelWidth}
            labelPosition={labelPosition}
            tooltip={tooltip}
            required={required}
        >
            {selectElem}
        </SMCField>
    );
}

const useStyles = makeStyles({
    select: {
        boxSizing: 'border-box',
        backgroundColor: '#fff',
        color: '#333',
        fontFamily: 'Open Sans',
        border: '1px solid #d2d5d9',
        paddingRight: '5px'
    },
    disabled: {
        color: '#545454',
        backgroundColor: '#EBEBE4',
        '&:focus': {
            border: '1px solid #d2d5d9'
        }
    }
});

SMCSelect.propTypes = {
    disabled: PropTypes.bool,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    inline: PropTypes.bool,
    onChange: PropTypes.func,
    className: PropTypes.string,
    style: PropTypes.object,
    label: PropTypes.node,
    labelWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    labelPosition: PropTypes.oneOf(['start', 'end']),
    tooltip: PropTypes.node,
    required: PropTypes.bool,
    children: PropTypes.node,
    ignoreDisabledContext: PropTypes.bool
}

export default React.memo(SMCSelect);